import React, { useEffect, useState, memo, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  Search,
  MessageCircle,
  Users,
  RefreshCw,
  Trash2,
  Shield,
  Clock,
  User,
  ChevronRight,
  MessagesSquare,
  ArrowLeftRight,
  Paperclip
} from 'lucide-react';

// Memoized message component for performance
const MessageBubble = memo(({ msg, onDelete }) => {
  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    return new Date(timestamp).toLocaleString('en-US', {
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true
    });
  };

  return (
    <div className="group flex items-start gap-3 py-2 px-3 hover:bg-muted/50 rounded-lg transition-colors">
      <Avatar className="w-8 h-8 flex-shrink-0">
        <AvatarFallback className="bg-gradient-to-br from-indigo-400 to-purple-500 text-white text-xs">
          {msg.sender_name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || '?'}
        </AvatarFallback>
      </Avatar>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium text-sm">{msg.sender_name}</span>
          <span className="text-xs text-muted-foreground">{formatTime(msg.created_at)}</span>
        </div>
        <p className="text-sm text-foreground/90 break-words">
          {msg.file_url && <Paperclip className="w-3 h-3 inline mr-1 text-primary" />}
          {msg.content}
        </p>
        {msg.deleted && <span className="text-xs text-red-500 italic">Deleted</span>}
      </div>
      <Button
        variant="ghost"
        size="icon"
        className="h-6 w-6 opacity-0 group-hover:opacity-100"
        onClick={() => onDelete(msg.id)}
      >
        <Trash2 className="w-3 h-3 text-red-500" />
      </Button>
    </div>
  );
});

// Chat list item component
const ChatListItem = memo(({ chat, isSelected, onClick, getInitials }) => {
  const isGroup = chat.type === 'group';
  
  return (
    <div
      className={`p-3 rounded-lg cursor-pointer transition-all ${
        isSelected 
          ? 'bg-primary/10 border-l-4 border-primary' 
          : 'hover:bg-muted/50 border-l-4 border-transparent'
      }`}
      onClick={onClick}
    >
      <div className="flex items-center gap-3">
        <Avatar className={`w-10 h-10 ${isGroup ? 'bg-gradient-to-br from-emerald-400 to-teal-500' : 'bg-gradient-to-br from-indigo-400 to-purple-500'}`}>
          <AvatarFallback className="text-white text-sm">
            {isGroup ? <Users className="w-5 h-5" /> : getInitials(chat.display_name)}
          </AvatarFallback>
        </Avatar>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="font-medium text-sm truncate">{chat.display_name || chat.name || 'Chat'}</p>
            {isGroup && (
              <span className="text-[10px] px-1.5 py-0.5 bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 rounded">
                Group
              </span>
            )}
          </div>
          {!isGroup && chat.participants_info?.length === 2 && (
            <p className="text-xs text-muted-foreground flex items-center gap-1">
              <ArrowLeftRight className="w-3 h-3" />
              {chat.participants_info[0]?.name} ↔ {chat.participants_info[1]?.name}
            </p>
          )}
          {isGroup && (
            <p className="text-xs text-muted-foreground">
              {chat.participants_info?.length || 0} members
            </p>
          )}
        </div>
        <ChevronRight className="w-4 h-4 text-muted-foreground" />
      </div>
      {chat.last_message && (
        <p className="text-xs text-muted-foreground mt-2 truncate pl-13">{chat.last_message}</p>
      )}
    </div>
  );
});

const ChatMonitorPage = () => {
  const { api } = useAuth();
  const [chats, setChats] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  
  const [stats, setStats] = useState({
    totalChats: 0,
    directChats: 0,
    groupChats: 0,
    totalMessages: 0
  });

  const fetchChats = useCallback(async () => {
    try {
      const response = await api.get('/admin/chat-monitor/chats');
      const chatList = response.data.chats || [];
      setChats(chatList);
      setStats({
        totalChats: chatList.length,
        directChats: chatList.filter(c => c.type === 'direct').length,
        groupChats: chatList.filter(c => c.type === 'group').length,
        totalMessages: response.data.total_messages || 0
      });
    } catch (error) {
      console.error('Error fetching chats:', error);
    }
  }, [api]);

  const fetchChatMessages = useCallback(async (chatId) => {
    setMessagesLoading(true);
    try {
      const response = await api.get(`/admin/chat-monitor/messages?chat_id=${chatId}&limit=200`);
      setMessages(response.data.messages?.reverse() || []);
    } catch (error) {
      console.error('Error fetching messages:', error);
      setMessages([]);
    } finally {
      setMessagesLoading(false);
    }
  }, [api]);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await fetchChats();
      setLoading(false);
    };
    init();
  }, [fetchChats]);

  useEffect(() => {
    if (selectedChat) {
      fetchChatMessages(selectedChat.id);
    }
  }, [selectedChat, fetchChatMessages]);

  const handleDeleteMessage = async (messageId) => {
    if (!window.confirm('Are you sure you want to delete this message?')) return;
    try {
      await api.delete(`/chat/messages/${messageId}`);
      toast.success('Message deleted');
      if (selectedChat) {
        fetchChatMessages(selectedChat.id);
      }
    } catch (error) {
      toast.error('Failed to delete message');
    }
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  // Filter chats based on search and tab
  const filteredChats = chats.filter(chat => {
    const matchesSearch = !searchQuery || 
      chat.display_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      chat.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      chat.participants_info?.some(p => p.name?.toLowerCase().includes(searchQuery.toLowerCase()));
    
    const matchesTab = activeTab === 'all' || 
      (activeTab === 'direct' && chat.type === 'direct') ||
      (activeTab === 'group' && chat.type === 'group');
    
    return matchesSearch && matchesTab;
  });

  return (
    <div className="p-6 h-[calc(100vh-80px)]">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent flex items-center gap-3">
            <Shield className="w-8 h-8 text-indigo-600" />
            Chat Monitor
          </h1>
          <p className="text-muted-foreground mt-1">View and manage all conversations</p>
        </div>
        <Button onClick={fetchChats} variant="outline" className="gap-2">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <Card className="border-0 shadow-lg bg-gradient-to-br from-indigo-500 to-purple-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <MessageCircle className="w-8 h-8 opacity-80" />
              <div>
                <p className="text-white/80 text-xs">Total Chats</p>
                <p className="text-2xl font-bold">{stats.totalChats}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-lg bg-gradient-to-br from-blue-500 to-cyan-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <User className="w-8 h-8 opacity-80" />
              <div>
                <p className="text-white/80 text-xs">Direct Chats</p>
                <p className="text-2xl font-bold">{stats.directChats}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-lg bg-gradient-to-br from-emerald-500 to-teal-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Users className="w-8 h-8 opacity-80" />
              <div>
                <p className="text-white/80 text-xs">Group Chats</p>
                <p className="text-2xl font-bold">{stats.groupChats}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-lg bg-gradient-to-br from-amber-500 to-orange-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <MessagesSquare className="w-8 h-8 opacity-80" />
              <div>
                <p className="text-white/80 text-xs">Messages</p>
                <p className="text-2xl font-bold">{messages.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content - Split View */}
      <div className="flex gap-6 h-[calc(100%-220px)]">
        {/* Chat List Panel */}
        <Card className="w-[380px] flex-shrink-0 border-0 shadow-xl flex flex-col">
          <CardHeader className="border-b p-4">
            <div className="space-y-3">
              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="grid grid-cols-3 w-full">
                  <TabsTrigger value="all" className="text-xs">All ({stats.totalChats})</TabsTrigger>
                  <TabsTrigger value="direct" className="text-xs">1-on-1 ({stats.directChats})</TabsTrigger>
                  <TabsTrigger value="group" className="text-xs">Groups ({stats.groupChats})</TabsTrigger>
                </TabsList>
              </Tabs>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search chats or users..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 h-9"
                />
              </div>
            </div>
          </CardHeader>
          <ScrollArea className="flex-1">
            <div className="p-2 space-y-1">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="w-6 h-6 animate-spin text-primary" />
                </div>
              ) : filteredChats.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <MessageCircle className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p className="text-sm">No conversations found</p>
                </div>
              ) : (
                filteredChats.map(chat => (
                  <ChatListItem
                    key={chat.id}
                    chat={chat}
                    isSelected={selectedChat?.id === chat.id}
                    onClick={() => setSelectedChat(chat)}
                    getInitials={getInitials}
                  />
                ))
              )}
            </div>
          </ScrollArea>
        </Card>

        {/* Messages Panel */}
        <Card className="flex-1 border-0 shadow-xl flex flex-col">
          {selectedChat ? (
            <>
              {/* Chat Header */}
              <CardHeader className="border-b p-4 bg-gradient-to-r from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800">
                <div className="flex items-center gap-4">
                  <Avatar className={`w-12 h-12 ${selectedChat.type === 'group' ? 'bg-gradient-to-br from-emerald-400 to-teal-500' : 'bg-gradient-to-br from-indigo-400 to-purple-500'}`}>
                    <AvatarFallback className="text-white">
                      {selectedChat.type === 'group' ? <Users className="w-6 h-6" /> : getInitials(selectedChat.display_name)}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <CardTitle className="text-lg">{selectedChat.display_name || selectedChat.name}</CardTitle>
                    <p className="text-sm text-muted-foreground">
                      {selectedChat.type === 'group' 
                        ? `${selectedChat.participants_info?.length || 0} members`
                        : selectedChat.participants_info?.map(p => p.name).join(' ↔ ')
                      }
                    </p>
                  </div>
                </div>
              </CardHeader>

              {/* Messages */}
              <ScrollArea className="flex-1 p-4">
                {messagesLoading ? (
                  <div className="flex items-center justify-center h-full">
                    <RefreshCw className="w-6 h-6 animate-spin text-primary" />
                  </div>
                ) : messages.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
                    <MessagesSquare className="w-16 h-16 mb-4 opacity-30" />
                    <p>No messages in this conversation</p>
                  </div>
                ) : (
                  <div className="space-y-1">
                    {messages.map(msg => (
                      <MessageBubble key={msg.id} msg={msg} onDelete={handleDeleteMessage} />
                    ))}
                  </div>
                )}
              </ScrollArea>

              {/* Footer */}
              <div className="border-t p-3 bg-muted/30">
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <div className="flex items-center gap-2">
                    <Clock className="w-3 h-3" />
                    {messages.length} messages in conversation
                  </div>
                  <span>Admin view only - Messages cannot be sent</span>
                </div>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-950">
              <div className="text-center">
                <div className="w-24 h-24 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center mx-auto mb-4">
                  <Shield className="w-12 h-12 text-white" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Chat Monitor</h3>
                <p className="text-muted-foreground max-w-sm">
                  Select a conversation from the left panel to view messages between users
                </p>
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default ChatMonitorPage;
